import type { ButtonHTMLAttributes, ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'default' | 'danger' | 'ghost'
export type ButtonSize = 'sm' | 'base'

/**
 * Quatro variantes, e cada uma existe por um motivo:
 *
 *  `primary`  a ação que a caixa de diálogo foi aberta para fazer — uma por tela.
 *  `default`  tudo o mais que escreve: editar, filtrar, cancelar.
 *  `danger`   o que não tem volta: revogar, remover, desativar.
 *  `ghost`    ação de linha de tabela, onde uma borda por linha vira grade.
 *
 * `danger` é a única que carrega cor de status sem um rótulo de status ao lado,
 * e por isso o texto do botão sempre diz o verbo destrutivo — "Revogar", não
 * "Confirmar". A cor reforça, não informa.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'border border-accent bg-accent text-on-accent hover:bg-accent-quiet hover:border-accent-quiet hover:text-fg',
  default: 'border border-line-strong text-fg-secondary hover:bg-inset hover:text-fg',
  danger: 'border border-crit text-crit hover:bg-crit hover:text-on-status',
  ghost: 'border border-transparent text-fg-muted hover:bg-inset hover:text-fg',
}

const SIZE: Record<ButtonSize, string> = {
  sm: 'px-1.5 py-0.5 text-micro',
  base: 'px-2.5 py-1 text-sm',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Desabilita enquanto a requisição está no ar. */
  busy?: boolean
  children: ReactNode
}

export function Button({
  variant = 'default',
  size = 'base',
  busy = false,
  disabled,
  className = '',
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled === true || busy}
      aria-busy={busy || undefined}
      className={`inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap uppercase tracking-label disabled:cursor-not-allowed disabled:opacity-45 ${VARIANT[variant]} ${SIZE[size]} ${className}`}
      style={{
        transition:
          'color var(--dur-fast) var(--ease-swift), background-color var(--dur-fast) var(--ease-swift), border-color var(--dur-fast) var(--ease-swift)',
      }}
      {...rest}
    >
      {children}
    </button>
  )
}
